import { updateFreshpackText } from '@/js/product-card-helpers';
export const STORAGE_KEY = 'pxl:subscription-products';

/**
 * Product Subscription Manager
 * ----------------------------------
 * A class for managing product subscriptions on the PLP
 */
export class ProductSubscriptionManager {
  /**
   * @param {HTMLElement} element - The element to initialize the filter manager on
   */
  constructor(element) {
    this.initVariables(element);
    this.init();
  }

  init() {
    this.initSubscriptionPanel();
    this.initSubscriptionProductCards();
    this.setLocalSubscriptionState();
  }

  /**
   * Initialize all variables
   * @param {HTMLElement} element
   * @private
   */
  initVariables(element) {
    this.element = element;

    // Selectors for the subscription product card
    this.selectors = {
      card: 'subscription-product-card',
      quantity: 'quantity-input',
      frequency: '[data-frequency-selector]',
      frequencyInput: 'input:checked',
      quantityInput: '[data-quantity-input]'
    };
  }

  /**
   * Initializes the event listeners for the subscription product cards
   * @returns {void}
   * @private
   */
  initSubscriptionProductCards() {
    const cards = this.element.querySelectorAll(this.selectors.card);

    for (const card of cards) {
      this.bindCardEvents(card);
      updateFreshpackText(card, 1);
    }

    // For all quantity inputs check if the button should be disabled or not
    const quantityInputs = this.element.querySelectorAll(
      this.selectors.quantity
    );

    for (const quantityInput of quantityInputs) {
      quantityInput?.disableAndEnableButtons();
    }
  }

  /**
   * Binds the event listeners to the subscription product card
   * @param {HTMLElement} card
   * @returns {void}
   * @private
   */
  bindCardEvents(card) {
    const quantityInput = card.querySelector(this.selectors.quantity);
    const frequencySelector = card.querySelector(this.selectors.frequency);

    // Quantity change event
    quantityInput?.addEventListener('pxl:quantityChange', event => {
      this.handleProductUpdate(card, event.detail.quantity);
      updateFreshpackText(card, event.detail.quantity);
    });

    // Frequency change event
    frequencySelector?.addEventListener('change', () => {
      const quantity = this.getQuantityValue(card);
      this.handleProductUpdate(card, quantity);
    });
  }

  /**
   * Handles the product update
   * @param {HTMLElement} card
   * @param {Number} quantity
   * @returns {void}
   * @private
   */
  handleProductUpdate(card, quantity) {
    const selectedFrequency = card.querySelector(this.selectors.frequency);

    if (!selectedFrequency) return;

    const selectedFrequencyInput = selectedFrequency.querySelector(
      this.selectors.frequencyInput
    );

    if (!selectedFrequencyInput) return;

    // Store to local storage
    this.updateSubscriptionProducts({
      variantId: card.dataset.variantId,
      quantity: Number(quantity),
      sellingPlan: selectedFrequencyInput.value
    });
  }
  /**
   * Gets the quantity value from the quantity input
   * @param {HTMLElement} card
   * @returns {Number}
   * @private
   */
  getQuantityValue(card) {
    const quantityInput = card.querySelector(this.selectors.quantity);
    return quantityInput?.querySelector(this.selectors.quantityInput)?.value;
  }

  /**
   * Updates the UI with the quantity and frequency selector value from local storage
   * @returns {void}
   * @private
   */
  setLocalSubscriptionState() {
    // Load local storage quantity and frequency selector value
    const sessionStorageProducts = JSON.parse(
      sessionStorage.getItem(STORAGE_KEY) || '[]'
    );

    const subscriptionContent = this.element.querySelector(
      '[data-subscription-content]'
    );

    if (!subscriptionContent) return;

    const subscriptionProducts = subscriptionContent.querySelectorAll(
      'subscription-product-card'
    );

    if (sessionStorageProducts.length > 0) {
      // For each product in local storage, find the matching subscription product card and update the quantity and frequency selector value
      for (const sessionStorageProduct of sessionStorageProducts) {
        const subscriptionProduct = [...subscriptionProducts].find(
          product =>
            Number.parseInt(product.dataset.variantId) ===
            Number.parseInt(sessionStorageProduct.variantId)
        );

        if (!subscriptionProduct) continue;

        // Add the class valid to the card
        subscriptionProduct.classList.add('valid');

        // A delay is needed to ensure the subscription product card is rendered
        setTimeout(() => {
          updateFreshpackText(
            subscriptionProduct,
            sessionStorageProduct.quantity
          );
        }, 100);

        // Update the frequency and quantity selected from local storage
        const quantityInput =
          subscriptionProduct.querySelector('quantity-input');

        // Update the value and trigger the change
        quantityInput?.updateValue(sessionStorageProduct.quantity);

        const selectedFrequencyContainer = subscriptionProduct.querySelector(
          '[data-frequency-selector]'
        );

        if (!selectedFrequencyContainer) continue;

        // get input type radio with name equal to sellingPlan
        const selectedFrequency = selectedFrequencyContainer.querySelector(
          `input[type="radio"][value="${sessionStorageProduct.sellingPlan}"]`
        );
        if (!selectedFrequency) continue;

        selectedFrequency.checked = true;
      }
    }
  }

  /**
   * Initializes the subscription panel
   * @returns {void}
   * @private
   */
  initSubscriptionPanel() {
    customElements.whenDefined('subscription-panel').then(() => {
      const subscriptionPanel = document.querySelector('subscription-panel');
      if (subscriptionPanel) {
        subscriptionPanel.showHidePanel();
      }
    });
  }

  /**
   * Updates or adds a subscription product
   * @param {Object} subscriptionProduct - The subscription product data
   * @param {string} subscriptionProduct.variantId - The variant ID of the product
   * @param {number} subscriptionProduct.quantity - The quantity of the product
   * @param {string} subscriptionProduct.sellingPlan - The selling plan value for subscription
   */
  updateSubscriptionProducts({ variantId, quantity, sellingPlan }) {
    if (typeof quantity != 'number' || !sellingPlan) {
      return;
    }

    const existingProducts = JSON.parse(
      sessionStorage.getItem(STORAGE_KEY) || '[]'
    );

    const productIndex = existingProducts.findIndex(product => {
      return Number(product.variantId) === Number(variantId);
    });
    const productData = { variantId, quantity, sellingPlan };

    const card = document.querySelector(
      `subscription-product-card[data-variant-id="${variantId}"]`
    );

    if (productIndex === -1) {
      if (quantity > 0) {
        // Add the product to the session storage
        existingProducts.push(productData);
        // add the class valid to the card
        card?.classList.add('valid');
      }
    } else if (quantity > 0) {
      // Update the product in the session storage
      existingProducts[productIndex] = productData;
    } else {
      this.deselectFrequencySelector(existingProducts, productIndex);
      // Remove the product from the session storage
      existingProducts.splice(productIndex, 1);
      // Remove the valid class from the card
      card?.classList.remove('valid');
    }

    // Store to sessionStorage
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(existingProducts));

    // Dispatch a custom event to notify that the subscription products have been updated
    globalThis.dispatchEvent(
      new CustomEvent('subscriptionProductsUpdated', {
        detail: {
          subscriptionProducts: existingProducts
        }
      })
    );
  }

  /**
   * Deselects the frequency selector
   * @param {Array} existingProducts - The existing products in the session storage
   * @param {Number} productIndex - The index of the product to deselect the frequency selector for
   * @returns {void}
   * @private
   */
  deselectFrequencySelector(existingProducts, productIndex) {
    const frequencySelector = existingProducts[productIndex].sellingPlan;
    const frequencySelectorElement = document.querySelector(
      `input[type="radio"][value="${frequencySelector}"]`
    );
    if (frequencySelectorElement) {
      frequencySelectorElement.checked = false;
    }
  }
}
